/* ============================================================
   SUIS REKA BENTUK v3.9  —  config/ui_v39
   ============================================================
   Satu suis untuk seluruh revamp v3.9. Selagi ia MATI, pelanggan
   nampak app lama walaupun kod v3.9 sudah penuh dalam bundle
   production. Ini yang membolehkan kerja revamp berjalan tanpa
   risiko satu deploy tersasar mendedahkan app separuh siap.

   ⛔ FAIL-CLOSED — ini KEBALIKAN daripada suis lain dalam projek
      ini (product_split & extension_gate fail-OPEN). Sebabnya:
      di sana "gagal baca" bermaksud teruskan perangai lama yang
      SELAMAT; di sini perangai lama juga yang selamat, tetapi
      "lama" = UI SEDIA ADA. Jadi apa-apa keraguan — doc tiada,
      rules menolak, rangkaian putus, ralat parse — mesti jatuh
      ke UI LAMA, bukan ke v3.9.
      JANGAN tukar mana-mana `false` di bawah kepada `true`
      sebagai "default yang munasabah".

   Bentuk doc:
     config/ui_v39 = { enabled: bool, adminPreview: bool }

     enabled:true                   → SEMUA pengguna dapat v3.9
     enabled:false + adminPreview   → admin sahaja (untuk semak
                                      di production sebelum lancar)
     doc tiada / ralat / rules tolak → semua orang kekal UI lama

   ⚠️ PRASYARAT LANCAR: rule bacaan `config/ui_v39` mesti
      di-deploy dahulu (firestore.rules). Selagi belum, pelanggan
      bukan-admin LANGSUNG tidak boleh membaca doc ini, jadi v3.9
      mustahil hidup untuk mereka walaupun `enabled` ditetapkan
      true. Itu satu lagi kunci — bukan masalah.

   Ujian tempatan: jalankan `localStorage.sesaat_ui_v39 = '1'`
   dalam console, kemudian refresh. Hanya berkesan pada dev
   server (import.meta.env.DEV) — ia tiada kesan di production.

   ⭐⭐ [2026-08-26] SUBSCRIBE HANYA SELEPAS LOG MASUK — jangan undur.
      Rules `config/ui_v39` menolak bacaan tanpa auth. Dahulu hook ini
      melanggan sebaik app dibuka — iaitu semasa SKRIN LOGIN — jadi
      server menolak, dan permission-denied MENAMATKAN listener
      Firestore secara kekal. Effect tidak berjalan semula untuk
      bukan-admin (deps `[isAdmin]` kekal false), maka SETIAP pelanggan
      yang log masuk mendapat rupa LAMA sepanjang sesi itu, walaupun
      suis enabled:true. Admin terlepas kerana `isAdmin` bertukar
      false→true selepas log masuk dan itu kebetulan membuka semula
      langganan — sebab itulah bug ini tidak kelihatan pada akaun
      admin. Kini langganan diikat pada uid: dibuka HANYA selepas
      auth sedia, dan dibuka SEMULA setiap kali pengguna log masuk.

   ⭐ CACHE PER-BROWSER `sesaat_ui_v39_cache` — nilai TERAKHIR yang
      DISAHKAN dari Firestore. Selepas log masuk ia dipapar serta-
      merta supaya tiada kelipan rupa lama sementara snapshot pertama
      tiba. Ia BUKAN suis paksa (itu `sesaat_ui_v39`, dev sahaja):
      snapshot hidup sentiasa membetulkannya, dan ralat baca SENGAJA
      tidak menulis cache — ralat ialah keraguan, bukan pengetahuan.
      Fail-closed kekal dipegang: belum log masuk, browser pertama
      kali (tiada cache), atau ralat baca → rupa LAMA.
   ============================================================ */

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';

/**
 * Suis ujian tempatan — dev server sahaja, tiada makna di production.
 *
 * Pulangan: `true` paksa v3.9 · `false` paksa rupa lama · `null` tiada
 * pendapat (ikut Firestore). `null` PENTING: tanpanya tiada cara memaksa
 * rupa LAMA sebaik doc `config/ui_v39` wujud, kerana admin mendapat
 * pratonton secara automatik — dan itulah keadaan sepanjang hari lancar.
 *
 * Dua laluan, alamat didahulukan:
 *   ?v39=1 / ?v39=0   — satu tab sahaja. Inilah cara membanding: buka
 *                       DUA tab bersebelahan, satu setiap rupa.
 *   localStorage      — melekat pada browser sehingga dibuang.
 *
 * ⚠️ Kedua-duanya hanya dibaca semasa halaman DIMUAT. Menukar nilai
 *    dalam console tidak mengubah skrin sehingga di-refresh.
 */
function devOverride() {
    if (!import.meta.env.DEV) return null;
    try {
        const q = new URLSearchParams(window.location.search).get('v39');
        if (q === '1') return true;
        if (q === '0') return false;
        const v = localStorage.getItem('sesaat_ui_v39');
        if (v === '1') return true;
        if (v === '0') return false;
    } catch { /* abai — jatuh ke Firestore */ }
    return null;
}

/* Nilai terakhir yang DISAHKAN dari Firestore untuk browser ini — lihat nota
   ⭐ CACHE di kepala fail. Kunci SENGAJA berbeza daripada `sesaat_ui_v39`
   (suis paksa dev): mencampurkan keduanya akan menjadikan cache production
   satu suis paksa di localhost. */
const KUNCI_CACHE = 'sesaat_ui_v39_cache';
function bacaCache() {
    try { return localStorage.getItem(KUNCI_CACHE) === '1'; } catch { return false; }
}
function tulisCache(nilai) {
    try { localStorage.setItem(KUNCI_CACHE, nilai ? '1' : '0'); } catch { /* abai */ }
}

/**
 * Kira sama ada pengguna ini patut nampak reka bentuk v3.9.
 * @param {boolean} isAdmin — pengguna semasa ialah admin
 * @returns {boolean} true HANYA bila benar-benar pasti; selain itu false.
 */
export function useUiV39(isAdmin = false) {
    /* Paksaan dev dibaca SEKALI semasa mount — nota di kepala fail memang
       berkata ia hanya dibaca semasa halaman dimuat (tukar nilai = refresh). */
    const [paksa] = useState(() => devOverride());

    /* uid pengguna semasa — null selagi belum log masuk / Firebase Auth belum
       pulih dari storan. Inilah kunci fix 2026-08-26: langganan di bawah
       diikat pada uid, jadi ia dibuka SELEPAS auth sedia dan dibuka SEMULA
       apabila pengguna log masuk — bukan sekali sahaja semasa app dibuka. */
    const [uid, setUid] = useState(() => auth.currentUser?.uid || null);

    /* Jawapan Firestore untuk sesi log masuk semasa; null = belum tiba.
       Selagi null, nilai pulangan jatuh ke cache (nilai terakhir disahkan). */
    const [fs, setFs] = useState(null);

    useEffect(() => {
        let uidSebelum = auth.currentUser?.uid || null;
        return onAuthStateChanged(auth, (u) => {
            const baru = u ? u.uid : null;
            // Tukar akaun / log keluar → jawapan Firestore sesi lama tidak
            // lagi sah (adminPreview ikut siapa yang log masuk).
            if (baru !== uidSebelum) { uidSebelum = baru; setFs(null); }
            setUid(baru);
        });
    }, []);

    useEffect(() => {
        /* Paksaan tempatan menang dan TIDAK melanggan Firestore — dua tab
           yang membanding rupa tidak sepatutnya membuka dua pendengar doc.
           Belum log masuk pun TIDAK melanggan: rules menolak bacaan tanpa
           auth, dan permission-denied membunuh listener secara kekal —
           punca bug "pelanggan dapat rupa lama sepanjang sesi selepas
           login" (lihat nota ⭐⭐ di kepala fail). */
        if (paksa !== null || !uid) return;

        let hidup = true;
        let unsub = null;

        try {
            unsub = onSnapshot(
                doc(db, 'config', 'ui_v39'),
                (snap) => {
                    if (!hidup) return;
                    const d = (snap.exists() && snap.data()) || {};
                    // Pratonton admin: default HIDUP untuk admin apabila doc
                    // wujud, supaya admin boleh semak di production tanpa
                    // menghidupkannya untuk pelanggan. Matikan dengan
                    // menetapkan adminPreview:false.
                    const nilai = d.enabled === true ||
                        (snap.exists() && isAdmin && d.adminPreview !== false);
                    setFs(nilai);
                    tulisCache(nilai);
                },
                () => {
                    // Ralat baca → rupa lama untuk sesi ini. Cache SENGAJA
                    // tidak ditulis: ralat ialah keraguan, bukan pengetahuan.
                    if (hidup) setFs(false);
                },
            );
        } catch { /* onSnapshot gagal didaftar (amat jarang) — fs kekal null,
                     nilai pulangan jatuh ke cache sahaja. */ }

        return () => { hidup = false; try { unsub && unsub(); } catch { /* abai */ } };
    }, [isAdmin, uid, paksa]);

    /* Nilai DIKIRA semasa render, bukan di-setState dalam effect (peraturan
       lint react-hooks/set-state-in-effect):
       paksa dev → ikut paksa · belum log masuk → rupa lama (fail-closed) ·
       jawapan Firestore sudah tiba → ikut jawapan · belum tiba → nilai
       terakhir yang disahkan (cache), supaya tiada kelipan rupa lama. */
    if (paksa !== null) return paksa;
    if (!uid) return false;
    return fs !== null ? fs : bacaCache();
}
